import { useEffect } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { useTheme } from 'next-themes';

/**
 * Status Bar Configuration Hook
 *
 * Syncs iOS status bar appearance with app theme:
 * - Light mode: Dark text on light background
 * - Dark mode: Light text on dark background
 * - System: Follows device theme
 *
 * Also ensures status bar is always visible with proper overlay mode.
 *
 * Usage: Call once in root component (App.tsx)
 */
export const useStatusBarConfig = () => {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    // Only run on native platforms (iOS primarily, Android has different behavior)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const configureStatusBar = async () => {
      try {
        // Set status bar style based on theme
        const style = resolvedTheme === 'dark' ? Style.Dark : Style.Light;
        await StatusBar.setStyle({ style });

        // Set background color (iOS only)
        if (Capacitor.getPlatform() === 'ios') {
          const backgroundColor = resolvedTheme === 'dark' ? '#000000' : '#ffffff';
          await StatusBar.setBackgroundColor({ color: backgroundColor });
        }

        // Always show status bar
        await StatusBar.show();

        // Set overlay mode (content goes behind status bar)
        await StatusBar.setOverlaysWebView({ overlay: true });
      } catch (error) {
        console.error('Failed to configure status bar:', error);
      }
    };

    configureStatusBar();
  }, [resolvedTheme]);
};
